import Close from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AddOnRateType, InvoiceLineItemType, RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { Control, useWatch } from 'react-hook-form';

import NumberInput from '~components/FormFields/v2/NumberInput';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import isEnumValue from '~utils/isEnumValue';

import {
  getAmountError,
  SettlementLineDraft,
  SettlementSheetFormValues,
} from './settlementSheetForm';
import { isFreightLineItem } from './settlementSheetLines';

const columnTracks = {
  date: 'minmax(64px, 0.8fr)',
  truck: 'minmax(48px, 0.6fr)',
  material: 'minmax(80px, 1.2fr)',
  ticket: 'minmax(56px, 0.7fr)',
  deliveredQuantity: 'minmax(96px, 1.2fr)',
  type: 'minmax(56px, 0.7fr)',
  rateType: 'minmax(80px, 1fr)',
  billingQuantity: 'minmax(72px, 0.8fr)',
  rate: 'minmax(88px, 1fr)',
  total: 'minmax(80px, 0.9fr)',
  action: '32px',
};

export type SettlementSheetColumn = keyof typeof columnTracks;

const gridTemplateColumns = `${Object.entries(columnTracks)
  .map(([column, track]) => `[${column}] ${track}`)
  .join(' ')} [end]`;

const ROW_MIN_HEIGHT_FITTING_INPUTS_PX = 44;

export const settlementSheetRowSx = (theme: Theme) => ({
  alignItems: 'center',
  borderBottom: `1px solid ${theme.semantic.border}`,
  columnGap: 1.5,
  display: 'grid',
  gridTemplateColumns,
  minHeight: `${ROW_MIN_HEIGHT_FITTING_INPUTS_PX}px`,
  px: 2,
  py: 0.5,
});

interface SettlementSheetCellProps {
  children: React.ReactNode;
  column: SettlementSheetColumn;
  isRightAligned?: boolean;
  spansUntil?: SettlementSheetColumn;
}

export function SettlementSheetCell({
  children,
  column,
  isRightAligned = false,
  spansUntil,
}: SettlementSheetCellProps) {
  return (
    <Typography
      variant="body2"
      sx={{
        gridColumn: spansUntil ? `${column} / ${spansUntil}` : column,
        minWidth: 0,
        ...(isRightAligned ? { textAlign: 'right' } : {}),
      }}
    >
      {children}
    </Typography>
  );
}

export const settlementSheetCardSx = (theme: Theme) => ({
  backgroundColor: theme.semantic.surfaceRaised,
  border: `1px solid ${theme.semantic.border}`,
  borderRadius: '6px',
  p: 2,
});

export const SHEET_DATE_FORMAT = 'MMM D';

export const formatAccountingAmount = (
  amount: number,
  currencyFormatter: (value: number) => string,
) => (amount < 0 ? `(${currencyFormatter(-amount)})` : currencyFormatter(amount));

const amountAdornments = {
  currency: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
  percentage: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
};

type SettlementSheetAmountName =
  | `addOnCharges.${string}.${keyof SettlementLineDraft}`
  | `lineItems.${string}.${keyof SettlementLineDraft}`
  | `newAddOnCharges.${number}.${keyof SettlementLineDraft}`;

interface SettlementSheetAmountInputProps {
  adornment?: keyof typeof amountAdornments;
  column: SettlementSheetColumn;
  control: Control<SettlementSheetFormValues>;
  label: string;
  name: SettlementSheetAmountName;
}

export function SettlementSheetAmountInput({
  adornment,
  column,
  control,
  label,
  name,
}: SettlementSheetAmountInputProps) {
  const amount = useWatch({ control, name });

  return (
    <NumberInput
      control={control}
      manualError={getAmountError(amount)}
      name={name}
      slotProps={{
        htmlInput: { 'aria-label': label },
        ...(adornment ? { input: amountAdornments[adornment] } : {}),
      }}
      suppressErrorMessage
      sx={{ gridColumn: column }}
    />
  );
}

interface SettlementSheetRemoveButtonProps {
  label: string;
  onClick: () => void;
}

export function SettlementSheetRemoveButton({
  label,
  onClick,
}: SettlementSheetRemoveButtonProps) {
  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        onClick={onClick}
        size="small"
        sx={{ gridColumn: 'action', justifySelf: 'end' }}
      >
        <Close fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export const getLineItemTypeLabel = (lineItem: InvoiceLineItem) => {
  if (!isEnumValue(InvoiceLineItemType, lineItem.lineItemType)) {
    return '';
  }

  return isFreightLineItem(lineItem)
    ? t('approvals.driver_pay.settlements.pdf.freight')
    : t('approvals.driver_pay.settlements.pdf.material');
};

export const getLineItemRateTypeLabel = ({ rateType }: InvoiceLineItem) =>
  isEnumValue(RateType, rateType) ? getRateTypeLabel(rateType) : rateType;

export const getAddOnRateTypeLabel = (rateType: AddOnRateType) =>
  isEnumValue(AddOnRateType, rateType) ? rateTypeTextByAddOnRateType[rateType] : rateType;
